// PRD §6.2 — Zod schema per EventType.
//
// Backend POST /api/v1/events and mobile WatermelonDB import the same
// `eventDataSchemas` so validation is bit-identical on both ends. When a new
// EventType is added to the Prisma enum, this map is the single source of
// truth that tells the rest of the codebase what `data_json` looks like.
import { z } from 'zod';
import { CROTAL_REGEX } from './crotal.js';
const crotalString = z.string().regex(CROTAL_REGEX, 'Crotal ISO 11784 inválido');
const empty = z.object({}).strict();
export const eventDataSchemas = {
    // ─── Lifecycle ───────────────────────────────────────────────────────────
    // Phase 25 iter-29 (Lote V, G2) — animal entered LeyroChain from outside
    // the platform (purchased from a non-customer farm, imported from another
    // country, transferred from a different traceability system). Distinct
    // from `TRANSFER` (which is an animal moving between two LeyroChain
    // farms). EUDR audit can use `origin_country` + `origin_farm_external`
    // to demonstrate provenance from outside Galicia. `import_date` is the
    // date the animal physically arrived; `acquisition_price_cents` is
    // optional for ROI tracking. All fields optional so the gandeiro can
    // log "animal X is from outside" with minimal data and enrich later.
    IMPORT: z
        .object({
        origin_country: z.string().max(2).optional().describe('ISO 3166-1 alpha-2 (ES, PT, FR…)'),
        origin_farm_external: z.string().max(120).optional(),
        origin_rega_code: z.string().max(40).optional(),
        origin_traceability_system: z.string().max(40).optional(),
        import_date: z.string().datetime({ offset: true }).optional(),
        acquisition_price_cents: z.number().int().nonnegative().optional(),
        note: z.string().max(2000).optional(),
    })
        .strict(),
    // Piloto §F2 (2026-05-13) — `sire_mode` discriminates natural vs AI.
    // All fields stay optional so legacy BIRTH events (pre-phase-14) keep
    // validating; the UI uses sire_mode to show/hide the right branch.
    BIRTH: z
        .object({
        sire_mode: z.enum(['natural', 'AI']).optional(),
        mother_crotal: crotalString.optional(),
        father_crotal: crotalString.optional(),
        // AI branch — populated when sire_mode === 'AI'.
        semen_batch: z.string().min(1).max(80).optional(),
        semen_provider: z.string().min(1).max(120).optional(),
        semen_breed: z.string().max(80).optional(),
        weight_kg: z.number().positive().max(80).optional(),
    })
        .strict(),
    DEATH: z
        .object({
        cause: z.string().min(1).max(200).optional(),
        weight_kg: z.number().positive().max(2000).optional(),
    })
        .strict(),
    WEIGHT: z.object({ kg: z.number().positive().max(2000) }).strict(),
    TRANSFER: z.object({ from_farm_id: z.string().uuid(), to_farm_id: z.string().uuid() }).strict(),
    OWNERSHIP_TRANSFER: z
        .object({
        from_user_id: z.string().uuid(),
        to_user_id: z.string().uuid(),
        price: z.number().nonnegative().optional(),
    })
        .strict(),
    LEYROSPOT_INSTALLED: z.object({ leyrospot_uuid: z.string().uuid() }).strict(),
    AUCTION_LISTED: z
        .object({
        fair_id: z.string().min(1).default('silleda'),
        start_price: z.number().nonnegative().optional(),
    })
        .strict(),
    AUCTION_SOLD: z
        .object({ buyer_id: z.string().uuid(), final_price: z.number().nonnegative() })
        .strict(),
    // ─── Sanidade ────────────────────────────────────────────────────────────
    VACCINE: z
        .object({
        vaccine_name: z.string().min(1).max(120),
        dose: z.string().max(40).optional(),
        batch: z.string().max(40).optional(),
    })
        .strict(),
    MEDICATION: z
        .object({
        drug: z.string().min(1).max(120),
        dose: z.string().max(40).optional(),
        withdrawal_days: z.number().int().nonnegative().max(365).optional(),
    })
        .strict(),
    HEALTH_CHECK: z
        .object({
        result: z.enum(['OK', 'WARN', 'ALERT']),
        notes: z.string().max(2000).optional(),
    })
        .strict(),
    // ─── Cárnica ─────────────────────────────────────────────────────────────
    // Piloto §F8 (phase 16, 2026-05-13) — `kg` is optional so the gandeiro can
    // record a regime like "Pasto libre" where there is no specific dose. When
    // present, `kg` still has to be positive (was `.positive()` already; we keep
    // the bound but lift the requirement).
    FEED_RECORD: z
        .object({
        feed_type: z.string().min(1).max(80),
        kg: z.number().positive().optional(),
    })
        .strict(),
    DIET_CHANGE: z
        .object({
        ration_kg_dry_matter: z.number().nonnegative(),
        forage_pct: z.number().min(0).max(100),
        concentrate_pct: z.number().min(0).max(100),
        silage_kg: z.number().nonnegative().optional(),
        hay_kg: z.number().nonnegative().optional(),
    })
        .strict()
        .refine((d) => d.forage_pct + d.concentrate_pct <= 100.0001, {
        message: 'forage_pct + concentrate_pct cannot exceed 100',
    }),
    // ─── Leiteira ────────────────────────────────────────────────────────────
    // Piloto §F4 (2026-05-13) — `number` is now optional + auto-computed
    // server-side from the count of previous LACTATION_START events for the
    // animal. The mobile UI no longer surfaces it.
    LACTATION_START: z
        .object({
        number: z.number().int().positive().optional(),
        expected_end_date: z.string().datetime().optional(),
    })
        .strict(),
    LACTATION_END: z
        .object({
        number: z.number().int().positive(),
        total_liters: z.number().nonnegative(),
        peak_liters: z.number().nonnegative(),
    })
        .strict(),
    ESTRO: empty,
    // Piloto §F6 (2026-05-13) — `semen_breed` added (optional) for piloto users
    // who track the sire breed of the AI dose. Catalog lives in
    // `shared/src/constants/catalogs/breeds.ts`; "Outra" fallback supported.
    INSEMINATION: z
        .object({
        semen_batch: z.string().min(1).max(80),
        semen_provider: z.string().min(1).max(120),
        semen_breed: z.string().max(80).optional(),
    })
        .strict(),
    PREGNANCY_CHECK: z
        .object({
        result_positive: z.boolean(),
        gestation_days: z.number().int().min(0).max(330).optional(),
    })
        .strict(),
    CALVING: z
        .object({
        calf_crotal_id: crotalString.optional(),
        sex: z.enum(['M', 'F']),
        weight_kg: z.number().positive().max(80).optional(),
    })
        .strict(),
    // ─── Transporte (ADR-015 §5.1) ───────────────────────────────────────────
    // One TRANSPORT_PICKUP + one TRANSPORT_DELIVERY per animal per movement,
    // grouped via EventBatch. GPS + NFC scan are captured offline on the
    // transporter's device and travel inside `data_json` (the Event.gps_*
    // columns mirror them when present). `leyrospot_uuid` is the NFC tag
    // scanned to prove custody; optional because not every animal has a
    // LeyroSpot yet.
    TRANSPORT_PICKUP: z
        .object({
        movement_id: z.string().uuid(),
        transporter_id: z.string().uuid(),
        from_farm_id: z.string().uuid(),
        vehicle_plate: z.string().min(1).max(16),
        leyrospot_uuid: z.string().uuid().optional(),
        gps_lat: z.number().min(-90).max(90).optional(),
        gps_lng: z.number().min(-180).max(180).optional(),
        photo_url: z.string().url().optional(),
    })
        .strict(),
    TRANSPORT_DELIVERY: z
        .object({
        movement_id: z.string().uuid(),
        transporter_id: z.string().uuid(),
        to_farm_id: z.string().uuid().optional(),
        dest_label: z.string().min(1).max(160).optional(),
        leyrospot_uuid: z.string().uuid().optional(),
        gps_lat: z.number().min(-90).max(90).optional(),
        gps_lng: z.number().min(-180).max(180).optional(),
    })
        .strict(),
    OTHER: z.object({ note: z.string().min(1).max(2000) }).strict(),
};
export const EVENT_TYPES = Object.keys(eventDataSchemas);
/**
 * Parse `data_json` for a given event type, returning the validated payload
 * or throwing a ZodError. Throwing (instead of returning a Result) lets
 * Fastify's setErrorHandler funnel it to the same 400 response as schema
 * failures on the request body itself.
 */
export function parseEventData(type, raw) {
    return eventDataSchemas[type].parse(raw);
}
/**
 * Event types accesibles a un FARMER piloto V1. ADR-011 §10: agocha
 * eventos que requiren UUIDs / BUYER role / subasta dixital / formularios
 * imposibles. Os schemas Zod desos events seguen aceptables polo backend
 * (back-compat) — só desaparecen do EventTypePicker do mobile.
 *
 * Excluídos vs EVENT_TYPES:
 *   - TRANSFER / OWNERSHIP_TRANSFER (UUIDs internos imposibles)
 *   - LEYROSPOT_INSTALLED (créase via NFC pairing — Phase 17)
 *   - AUCTION_LISTED / AUCTION_SOLD (subasta dixital — V1.5)
 *   - DIET_CHANGE (5 campos % imposible — substitúe FEED_RECORD; V2)
 */
export const V1_PILOTO_EVENT_TYPES = [
    'IMPORT',
    'BIRTH',
    'DEATH',
    'WEIGHT',
    'VACCINE',
    'MEDICATION',
    'HEALTH_CHECK',
    'FEED_RECORD',
    'LACTATION_START',
    'LACTATION_END',
    'ESTRO',
    'INSEMINATION',
    'PREGNANCY_CHECK',
    'CALVING',
    'OTHER',
];
//# sourceMappingURL=event.zod.js.map